import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import translations from 'services/translations/translations';

import OfferDashboard from './OfferDashboard';
import OfferSales from './OfferSales';
import OpenSuppliers from './OpenSuppliers';
import OpenWorkTypes from './OpenWorkTypes';
import SalesProOffers from './SalesProOffers';

const OVERVIEW = 'overview';
const SALES = 'sales';
const SALES_PRO = 'salespro';
const WORK_TYPES = 'work-types';
const SUPPLIERS = 'suppliers';
const VIEWS = [OVERVIEW, SALES, SALES_PRO, WORK_TYPES, SUPPLIERS];

/**
 * The offer reports as tabs: the dashboard of the period and the open preliminary offers and
 * offers by work type. The chosen tab is in the address (?view=work-types), so it can be
 * linked and stays on a reload.
 */
const OfferReports = (): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation([translations.offers]);

  const requested = new URLSearchParams(location.search).get('view');
  const view = requested && VIEWS.includes(requested) ? requested : OVERVIEW;

  const tabs = (
    <div className="offer-reports-tabs" role="tablist">
      {VIEWS.map((key) => (
        <button
          aria-selected={key === view}
          className={key === view ? 'active' : ''}
          key={key}
          onClick={() =>
            history.replace(
              key === OVERVIEW ? location.pathname : `?view=${key}`
            )
          }
          role="tab"
          type="button"
        >
          {t(`${translations.offers}:dashboard.tabs.${key}`)}
        </button>
      ))}
    </div>
  );

  // The offers being calculated, sent and ordered with their sales and margins
  if (view === SALES) {
    return <OfferSales tabs={tabs} />;
  }

  // The offers imported from SalesPro, the earlier offer program
  if (view === SALES_PRO) {
    return <SalesProOffers tabs={tabs} />;
  }

  if (view === WORK_TYPES) {
    return <OpenWorkTypes tabs={tabs} />;
  }

  // The materials of the open offers by the suppliers of the registry
  if (view === SUPPLIERS) {
    return <OpenSuppliers tabs={tabs} />;
  }

  return <OfferDashboard tabs={tabs} />;
};

export default OfferReports;
